/**
 * ResidentServiceEventCard — Section 2 (iter-140).
 *
 * Renders the resident-lens view of a Service Event on the PWA home:
 *   • Pre-service (Scheduled / In Progress): service name + scheduled
 *     time + resident prep instructions.
 *   • Post-service (Completed): service name + scheduled + actual
 *     completion time + units serviced + exception notice with TKT-
 *     number if one was raised for this resident.
 *
 * Zero operational data leak — no exception counts, no route details,
 * no labor notes.
 *
 * Polls /api/service-events/resident every 30s so the card auto-updates
 * on status transitions without manual refresh.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarClock, CheckCircle2, ClipboardList, AlertCircle } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';

const GOLD = '#C9A962';

// Iter-141 · Section 5 — soft chime played once per pre → post-service
// flip. Uses the Web Audio API (no external asset). Respects device
// mute policy: if the AudioContext is suspended (autoplay policy) we
// silently drop the tone and let the next user interaction unlock it.
let _sharedAudioCtx = null;
function playCompletionChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!_sharedAudioCtx) _sharedAudioCtx = new Ctx();
    const ctx = _sharedAudioCtx;
    if (ctx.state === 'suspended') {
      // Autoplay-blocked. Skip — the next user interaction on the page
      // will resume the context via the global unlock listener below.
      return;
    }
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    // Two soft tones — C6 → E6 — 240ms each, cross-faded.
    osc.frequency.setValueAtTime(1046.5, now);
    osc.frequency.setValueAtTime(1318.5, now + 0.22);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.65);
  } catch { /* silent — audio is best-effort */ }
}

// Unlock the shared AudioContext on the first user gesture so future
// chimes trigger silently without another user action.
if (typeof window !== 'undefined' && !window.__necsAudioUnlock) {
  window.__necsAudioUnlock = true;
  const unlock = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!_sharedAudioCtx) _sharedAudioCtx = new Ctx();
      if (_sharedAudioCtx.state === 'suspended') _sharedAudioCtx.resume();
    } catch { /* ignore */ }
  };
  ['pointerdown', 'keydown', 'touchstart'].forEach((ev) =>
    window.addEventListener(ev, unlock, { once: true, passive: true }),
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch { return iso; }
}

export default function ResidentServiceEventCard() {
  const [data, setData] = useState({ post: [], upcoming: [] });
  // Track which service_event_ids we've already chimed for so re-polls
  // do not repeat the sound. Also swallow the initial page-load
  // hydration — chime only when a status transitions during this
  // session.
  const chimedIdsRef = useRef(new Set());
  const initialSyncRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/service-events/resident');
      const post = d.post || [];
      const upcoming = d.upcoming || [];
      setData({ post, upcoming });

      // Iter-141 · Section 5 — chime once when a post-service card
      // appears that wasn't in our chimed set. Skip on the very first
      // load so we don't chime for events already completed before the
      // resident opened the app (no chime on manual page load).
      if (!initialSyncRef.current) {
        post.forEach((c) => c?.service_event_id && chimedIdsRef.current.add(c.service_event_id));
        initialSyncRef.current = true;
      } else {
        for (const c of post) {
          const id = c?.service_event_id;
          if (id && !chimedIdsRef.current.has(id)) {
            chimedIdsRef.current.add(id);
            playCompletionChime();
            break; // one chime per refresh cycle even if multiple flipped
          }
        }
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const postCard = data.post[0];
  const upcomingCard = data.upcoming[0];

  if (!postCard && !upcomingCard) return null;

  // Prefer the freshest post-service card. If it references the same
  // event as the top upcoming, hide the upcoming (event already done).
  const showPost = !!postCard;
  const showUpcoming = !!upcomingCard && (!postCard || postCard.service_event_id !== upcomingCard.service_event_id);

  return (
    <div className="space-y-3" data-testid="resident-service-event-cards">
      {showPost && <PostServiceCard c={postCard} />}
      {showUpcoming && <PreServiceCard c={upcomingCard} />}
    </div>
  );
}

function PreServiceCard({ c }) {
  return (
    <div className="rounded-2xl p-4 border border-[#C9A962]/40 bg-zinc-950/70" data-testid={`resident-preservice-card-${c.service_event_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock className="h-4 w-4" style={{ color: GOLD }} />
        <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: GOLD }}>Tonight's Service</p>
      </div>
      <p className="text-white text-base font-semibold" data-testid={`resident-preservice-name-${c.service_event_id}`}>
        {c.service_type_label}
      </p>
      <p className="text-xs text-zinc-400 mt-1">
        Scheduled: {fmtDate(c.scheduled_start)} · {fmtTime(c.scheduled_start)}
      </p>
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-zinc-900/60 border border-zinc-800 p-2.5">
        <ClipboardList className="h-4 w-4 shrink-0 mt-0.5" style={{ color: GOLD }} />
        <p className="text-[11px] text-zinc-300">
          {prepInstruction(c.service_type)}
        </p>
      </div>
    </div>
  );
}

function PostServiceCard({ c }) {
  const hasException = !!c.exception_ticket_number;
  return (
    <div className="rounded-2xl p-4 border border-emerald-600/40 bg-emerald-950/20" data-testid={`resident-postservice-card-${c.service_event_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">
          Service Completed
        </p>
      </div>
      <p className="text-white text-base font-semibold" data-testid={`resident-postservice-name-${c.service_event_id}`}>
        {c.service_type_label}
      </p>
      <p className="text-xs text-zinc-300 mt-1">
        {c.service_type_label} for tonight has been completed. Thank you.
      </p>
      <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]" data-testid={`resident-postservice-meta-${c.service_event_id}`}>
        <Cell label="Scheduled" value={fmtTime(c.scheduled_time)} />
        <Cell label="Completed" value={fmtTime(c.completion_time)} />
        <Cell label="Units serviced" value={c.units_serviced ?? '—'} />
      </div>
      {hasException && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-950/40 border border-red-600/40 p-2.5" data-testid={`resident-postservice-exception-${c.service_event_id}`}>
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-red-400" />
          <div>
            <p className="text-[11px] text-red-200 font-semibold">Exception logged — our team has been notified.</p>
            <p className="text-[10px] text-red-300 font-mono mt-0.5">Ticket {c.exception_ticket_number}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div className="rounded bg-zinc-900/60 border border-zinc-800 p-2">
      <p className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-xs text-white font-semibold">{value}</p>
    </div>
  );
}

function prepInstruction(serviceType) {
  const t = (serviceType || '').toLowerCase();
  if (t === 'valet_trash') return 'Place your bag outside your door by 8:00 PM. Bags should be tied and no larger than 13-gallon.';
  if (t === 'recycling') return 'Recycling bin will be picked up curbside. Sort clean recyclables only.';
  if (t === 'bulk_removal') return 'Leave the bulk item near your unit entry. Our team will haul it away.';
  if (t === 'pressure_washing') return 'Move any personal items away from the exterior areas being serviced.';
  if (t === 'floor_care' || t === 'floor_polishing' || t === 'floor_restoration')
    return 'Avoid the treated floor area for at least 2 hours after completion. Signage will be posted.';
  return 'No preparation required. Our team will handle everything.';
}
